<template>
	<div class="relative flex items-center justify-center">
		<div>
			<svg
				:viewBox="viewBox"
				class="progress"
				xmlns="http://www.w3.org/2000/svg"
			>
				<circle class="circle-background" />
				<!--
					EN-799: when maxTotal < 100 (alignment locked), the unreachable
					portion of the ring — e.g. the 40 Alignment points a resume can't
					earn until a target role resolves — renders as a distinct dashed
					arc rather than plain background, so it reads as "locked", not
					just "not yet earned".
				-->
				<circle
					v-if="maxTotal < 100 && !pending"
					class="circle-locked"
				/>
				<circle class="circle-progress" />
			</svg>
		</div>

		<span
			:class="progressClass"
			class="progress-value absolute items-center font-medium"
		>
			<!-- EN-941: the score alone, even out of 60; the dashed arc already shows what's locked. -->
			<template v-if="pending">—</template>
			<template v-else>{{ progress === 0 ? "_ _" : progress }}</template>
		</span>
	</div>
</template>

<script setup>
import { computed, toRefs } from 'vue';
import { computeLockedArc } from '../../utils/score-circle';

const PROGRESS_COLORS = {
	green: '#2A8176',
	yellow: '#F3894F',
	red: '#EB5757',
	gray: '#DBDBDC'
};

const LOCKED_COLOR = '#B7B0C9';

const props = defineProps({
	progress: {
		type: Number,
		default: 0,
	},
	// EN-799: the score's ceiling — 60 while alignment is locked/unresolved,
	// 100 once it's scored. Purely additive: components that never pass this
	// (interview reports, other score-circle usages) keep today's behavior.
	maxTotal: {
		type: Number,
		default: 100,
	},
	circleWidth: {
		type: Number,
		default: 50
	},
	strokeWidth: {
		type: Number,
		default: 4
	},
	progressClass: {
		type: String,
		default: ''
	},
	rotate: {
		type: Number,
		default: 270
	},
	// EN-812: the resume doesn't have enough real content to score yet, so the ring shows a
	// dash and an empty track rather than a number.
	pending: {
		type: Boolean,
		default: false,
	},
});

const { circleWidth, strokeWidth, progressClass, rotate, maxTotal, pending } = toRefs(props);
const progress = computed(() => (pending.value ? 0 : props.progress));

const radius = computed(() => circleWidth.value / 2 - strokeWidth.value);
const strokeDashArray = computed(() => 2 * Math.PI * radius.value);
const viewBox = computed(() => `0 0 ${circleWidth.value} ${circleWidth.value}`);
const strokeDashOffset = computed(() => strokeDashArray.value * ((100 - progress.value) / 100));
const rotateBy = computed(() => `${rotate.value}deg`);
const strokeWidthValue = computed(() => `${strokeWidth.value}px`);
const circleWidthValue = computed(() => `${circleWidth.value}px`);

const lockedArc = computed(() => computeLockedArc(strokeDashArray.value, maxTotal.value));
const lockedDashArray = computed(() => lockedArc.value.dashArray);
const lockedDashOffset = computed(() => lockedArc.value.dashOffset);

const progressColor = computed(() => {
	if (progress.value === 0) {
		return PROGRESS_COLORS.gray;
	} else if (progress.value < 50) {
		return PROGRESS_COLORS.red;
	} else if (progress.value < 75) {
		return PROGRESS_COLORS.yellow;
	}

	return PROGRESS_COLORS.green;
});
</script>

<style scoped>
.progress {
    width: v-bind(circleWidthValue);
    height: v-bind(circleWidthValue);
    transform: rotate(v-bind(rotateBy));
}

.circle-background,
.circle-progress,
.circle-locked {
    r: v-bind(radius);
    cx: 50%;
    cy: 50%;
    fill: none;
    stroke-width: v-bind(strokeWidthValue);
}

.circle-background {
    stroke: #EEF2FE;
}

.circle-progress {
    stroke: v-bind(progressColor);
    stroke-dasharray: v-bind(strokeDashArray);
    stroke-dashoffset: v-bind(strokeDashOffset);
    stroke-linecap: round;
}

.circle-locked {
    stroke: v-bind(LOCKED_COLOR);
    stroke-dasharray: v-bind(lockedDashArray);
    stroke-dashoffset: v-bind(lockedDashOffset);
    opacity: 0.5;
}

.progress-value {
    color: v-bind(progressColor);
}
</style>